/**
 * Profile mode (§5.1 #27, §7.7, PRD Profile mode).
 *
 * A simulated LinkedIn profile rendered from the **latest snapshot**: banner, avatar (media rules
 * apply), name, headline, about, location, follower/connection counts, flag badges and whatever
 * sections the scraper provided — positions, education, skills, languages and friends. `full_json`
 * is deliberately `unknown`, because the mapper preserves keys nobody has seen yet; the readers in
 * `lib/unknown.ts` make every section optional rather than assumed.
 *
 * A **company** page (`snapshot.kind === 'company'`) is the same chrome with its own body: employee
 * counts instead of connections, and company details / offices instead of experience and education.
 * The person sections are simply absent from a company's `full_json`, so the split is one branch on
 * the header and one on the section list — every reader below stays total either way.
 *
 * Below it, the change-history list (field, old → new, detected date). This mode ignores
 * Filter / Sort / Date Range by design. Every section is a kit island with its header outside
 * (redesign § Profile mode).
 */

import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ProfileKind, SubjectType } from '@lw/shared';
import { getViewProfile } from '../../../api/views';
import { QK } from '../../../api/queryKeys';
import { EmptyState, ErrorState, ExternalLinkIcon, MediaBlock, Spinner } from '../../../components';
import { formatInteger } from '../../../lib/format';
import {
  asNumber,
  asRecord,
  asString,
  firstString,
  periodText,
  recordsAt,
  stringsAt,
  type UnknownRecord,
} from '../../../lib/unknown';

type FlagKey = 'premium' | 'creator' | 'verified' | 'openToWork' | 'hiring' | 'influencer';

const FLAGS: { key: FlagKey; label: string }[] = [
  { key: 'premium', label: 'Premium' },
  { key: 'creator', label: 'Creator' },
  { key: 'verified', label: 'Verified' },
  { key: 'openToWork', label: 'Open to work' },
  { key: 'hiring', label: 'Hiring' },
  { key: 'influencer', label: 'Influencer' },
];

/** A company page has one flag of its own; the other five are person-only and never set. */
const COMPANY_FLAG_KEYS: FlagKey[] = ['verified'];

function flagsFor(kind: ProfileKind): { key: FlagKey; label: string }[] {
  return kind === 'company' ? FLAGS.filter((flag) => COMPANY_FLAG_KEYS.includes(flag.key)) : FLAGS;
}

/** A kit island with its header outside (redesign § Design language "Islands"). */
function ProfileSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="section">
      <div className="section-head">
        <h3 className="section-title">{title}</h3>
      </div>
      <div className="card">
        <div className="card__body">{children}</div>
      </div>
    </section>
  );
}

function PositionList({ entries }: { entries: UnknownRecord[] }) {
  if (entries.length === 0) return null;
  return (
    <ul className="profile-list">
      {entries.map((entry, index) => {
        const title = firstString(entry, 'title', 'position');
        const company = firstString(entry, 'companyName');
        const start = periodText(entry, 'startDate');
        const end = periodText(entry, 'endDate');
        const duration = firstString(entry, 'duration');
        const location = firstString(entry, 'location');
        const description = firstString(entry, 'description');
        const skills = stringsAt(entry, 'skills');
        return (
          <li className="profile-item" key={`${title ?? 'position'}-${index}`}>
            <div className="profile-item__head">
              <span className="profile-item__title">{title ?? 'Position'}</span>
              {company !== undefined ? <span className="muted">{company}</span> : null}
            </div>
            <div className="profile-item__meta">
              {start !== undefined || end !== undefined ? (
                <span>
                  {start ?? '?'} — {end ?? 'Present'}
                </span>
              ) : null}
              {duration !== undefined ? <span>{duration}</span> : null}
              {location !== undefined ? <span>{location}</span> : null}
            </div>
            {description !== undefined ? (
              <p className="profile-item__desc">{description}</p>
            ) : null}
            {skills.length > 0 ? (
              <p className="profile-item__tags">
                {skills.map((skill) => (
                  <span className="tag" key={skill}>
                    {skill}
                  </span>
                ))}
              </p>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

function EducationList({ entries }: { entries: UnknownRecord[] }) {
  if (entries.length === 0) return null;
  return (
    <ul className="profile-list">
      {entries.map((entry, index) => {
        const school = firstString(entry, 'schoolName');
        const degree = firstString(entry, 'degree');
        const field = firstString(entry, 'fieldOfStudy');
        const period = firstString(entry, 'period') ?? periodText(entry, 'startDate');
        // v3 keeps `description` (honours, projects, awards); v2 dropped it outright.
        const description = firstString(entry, 'description');
        return (
          <li className="profile-item" key={`${school ?? 'school'}-${index}`}>
            <div className="profile-item__head">
              <span className="profile-item__title">{school ?? 'School'}</span>
              {degree !== undefined || field !== undefined ? (
                <span className="muted">{[degree, field].filter(Boolean).join(' · ')}</span>
              ) : null}
            </div>
            {period !== undefined ? <div className="profile-item__meta">{period}</div> : null}
            {description !== undefined ? (
              <p className="profile-item__desc">{description}</p>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Contact links: the modeled `websites[]` (v3) plus the actor's `profileActions[]` entries that
 * carry a URL. Rendered as one line because that is how LinkedIn's contact card reads.
 */
function ContactLine({ full }: { full: unknown }) {
  const websites = stringsAt(full, 'websites');
  const actions = recordsAt(full, 'actions')
    .map((action) => ({
      url: firstString(action, 'url'),
      label: firstString(action, 'label'),
    }))
    .filter((action): action is { url: string; label: string | undefined } => action.url !== undefined);
  const links = [
    ...websites.map((url) => ({ url, label: undefined as string | undefined })),
    ...actions,
  ];
  if (links.length === 0) return null;
  return (
    <p className="profile-head__links">
      {links.map((link) => (
        <a
          className="tag"
          key={link.url}
          href={link.url}
          target="_blank"
          rel="noreferrer noopener"
        >
          {link.label ?? link.url.replace(/^https?:\/\/(?:www\.)?/u, '')}
        </a>
      ))}
    </p>
  );
}

/**
 * Company offices, HQ first. Every string is the actor's own (`parsed.text` and the office label);
 * nothing is composed from the address parts, which are kept in `full_json` for completeness.
 */
function LocationList({ entries }: { entries: UnknownRecord[] }) {
  if (entries.length === 0) return null;
  const ordered = [
    ...entries.filter((entry) => entry.headquarter === true),
    ...entries.filter((entry) => entry.headquarter !== true),
  ];
  return (
    <ul className="profile-list">
      {ordered.map((entry, index) => {
        const text = firstString(entry, 'text');
        const description = firstString(entry, 'description');
        const street = firstString(entry, 'line1');
        return (
          <li className="profile-item" key={`${text ?? 'location'}-${index}`}>
            <div className="profile-item__head">
              <span className="profile-item__title">{text ?? 'Location'}</span>
              {entry.headquarter === true ? <span className="tag">Headquarters</span> : null}
              {description !== undefined ? <span className="muted">{description}</span> : null}
            </div>
            {street !== undefined ? <div className="profile-item__meta">{street}</div> : null}
          </li>
        );
      })}
    </ul>
  );
}

/** One `label: value` row of the company details island. */
function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <li className="profile-item">
      <div className="profile-item__head">
        <span className="profile-item__title">{label}</span>
      </div>
      <div className="profile-item__meta">{children}</div>
    </li>
  );
}

function ExternalLink({ url }: { url: string }) {
  return (
    <a href={url} target="_blank" rel="noreferrer noopener">
      {url.replace(/^https?:\/\/(?:www\.)?/u, '')}
    </a>
  );
}

/** Website, call-to-action, company type and industries — the company's core identity. */
function CompanyDetails({ full }: { full: unknown }) {
  const record = asRecord(full);
  const website = asString(record?.website);
  const callToActionUrl = asString(record?.callToActionUrl);
  const companyType = firstString(record, 'companyType');
  const industries = recordsAt(full, 'industries');
  const rows: ReactNode[] = [];
  if (website !== undefined) {
    rows.push(
      <DetailRow label="Website" key="website">
        <ExternalLink url={website} />
      </DetailRow>,
    );
  }
  // Only when it says something the website line does not.
  if (callToActionUrl !== undefined && callToActionUrl !== website) {
    rows.push(
      <DetailRow label="Call to action" key="cta">
        <ExternalLink url={callToActionUrl} />
      </DetailRow>,
    );
  }
  if (companyType !== undefined) {
    rows.push(
      <DetailRow label="Company type" key="type">
        {companyType}
      </DetailRow>,
    );
  }
  for (const [index, industry] of industries.entries()) {
    const name = firstString(industry, 'name');
    if (name === undefined) continue;
    const hierarchy = firstString(industry, 'hierarchy');
    rows.push(
      <DetailRow label={index === 0 ? 'Industry' : ''} key={`industry-${name}`}>
        <span>{name}</span>
        {hierarchy !== undefined && hierarchy !== name ? (
          <span className="faint">{hierarchy}</span>
        ) : null}
      </DetailRow>,
    );
  }
  if (rows.length === 0) return null;
  return <ul className="profile-list">{rows}</ul>;
}

function TagGroup({ title, values }: { title: string; values: string[] }) {
  if (values.length === 0) return null;
  return (
    <section className="profile-tags">
      <h4 className="profile-tags__title">{title}</h4>
      <p className="profile-item__tags">
        {values.map((value) => (
          <span className="tag" key={value}>
            {value}
          </span>
        ))}
      </p>
    </section>
  );
}

export type ProfileModeProps = {
  subjectType: SubjectType;
  id: number;
  enabled: boolean;
  mediaEnabled: boolean;
  onScrapeNow: () => void;
};

export function ProfileMode({ subjectType, id, enabled, mediaEnabled, onScrapeNow }: ProfileModeProps) {
  const [memberId, setMemberId] = useState<number | undefined>(undefined);
  const params = { memberId };

  const query = useQuery({
    queryKey: QK.viewProfile(subjectType, id, params),
    queryFn: ({ signal }) => getViewProfile(subjectType, id, params, signal),
    enabled,
  });

  if (query.isPending) return <Spinner size="lg" label="Loading profile…" />;

  if (query.isError) {
    return (
      <ErrorState
        title="Couldn't load the profile"
        error={query.error}
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
      />
    );
  }

  const { snapshot, changeLog, members, neverScraped } = query.data;

  const switcher =
    members !== undefined && members.length > 0 ? (
      <label className="row profile__switcher">
        <span className="muted">Member</span>
        <select
          className="select"
          aria-label="Group member"
          value={memberId ?? snapshot?.memberId ?? members[0]?.id ?? ''}
          onChange={(event) => setMemberId(Number(event.target.value))}
        >
          {members.map((member) => (
            <option key={member.id} value={member.id}>
              {member.name ?? `Profile #${member.id}`}
            </option>
          ))}
        </select>
      </label>
    ) : null;

  if (snapshot === null || neverScraped === true) {
    return (
      <div className="profile">
        {switcher}
        <EmptyState
          title="Never scraped"
          description="No profile snapshot has been captured yet. Run a scrape to fill this in — the profile scraper stores the snapshot, and consecutive snapshots build the change history."
          action={
            <button type="button" className="btn-primary" onClick={onScrapeNow}>
              Scrape now
            </button>
          }
        />
      </div>
    );
  }

  const full = snapshot.full;
  const isCompany = snapshot.kind === 'company';
  const positions = snapshot.positions;
  const current = recordsAt(positions, 'current');
  const experience = recordsAt(positions, 'experience');
  const education = recordsAt(full, 'education');
  const skills = stringsAt(full, 'skills');
  const languages = stringsAt(full, 'languages');
  const certifications = stringsAt(full, 'certifications');
  const topSkills = stringsAt(full, 'topSkills');
  // Company body, read defensively out of `full_json` exactly like the person sections above.
  const specialities = stringsAt(full, 'specialities');
  const locations = recordsAt(full, 'locations');
  const employeeCount = asNumber(asRecord(full)?.employeeCount);
  const employeeRange = asRecord(asRecord(full)?.employeeCountRange);
  const rangeStart = asNumber(employeeRange?.start);
  const rangeEnd = asNumber(employeeRange?.end);
  const companyDetails = isCompany ? <CompanyDetails full={full} /> : null;
  // The lg avatar here is raw markup rather than <Avatar> (there is no MediaRef fallback path to
  // share), so the company shape class is applied by hand.
  const avatarClass = isCompany ? 'avatar avatar--lg avatar--company' : 'avatar avatar--lg';

  return (
    <div className="profile">
      {switcher}

      <section className="card profile-head">
        {snapshot.banner !== null ? (
          <MediaBlock
            media={snapshot.banner}
            mediaEnabled={mediaEnabled}
            className="profile-head__banner"
            ratio="4 / 1"
            alt=""
          />
        ) : (
          <div className="profile-head__banner profile-head__banner--empty" />
        )}

        <div className="profile-head__body">
          <div className="profile-head__avatar">
            {snapshot.avatar !== null ? (
              <MediaBlock
                media={snapshot.avatar}
                mediaEnabled={mediaEnabled}
                className={avatarClass}
                alt={snapshot.displayName ?? ''}
              />
            ) : (
              <span className={avatarClass}>
                {(snapshot.displayName ?? snapshot.slug).slice(0, 2).toUpperCase()}
              </span>
            )}
          </div>

          <h2 className="profile-head__name">{snapshot.displayName ?? snapshot.slug}</h2>
          {snapshot.headline !== null ? (
            <p className="profile-head__headline">{snapshot.headline}</p>
          ) : null}
          {snapshot.location !== null ? <p className="muted">{snapshot.location}</p> : null}

          <p className="profile-head__counts">
            {snapshot.followerCount !== null ? (
              <span>
                <strong>{formatInteger(snapshot.followerCount)}</strong> followers
              </span>
            ) : null}
            {!isCompany && snapshot.connectionsCount !== null ? (
              <span>
                <strong>{formatInteger(snapshot.connectionsCount)}</strong> connections
              </span>
            ) : null}
            {isCompany && employeeCount !== undefined ? (
              <span>
                <strong>{formatInteger(employeeCount)}</strong> employees
              </span>
            ) : null}
            {isCompany && rangeStart !== undefined ? (
              <span className="muted">
                {formatInteger(rangeStart)}
                {rangeEnd === undefined ? '+' : `–${formatInteger(rangeEnd)}`} range
              </span>
            ) : null}
          </p>

          <p className="profile-head__flags">
            {flagsFor(snapshot.kind)
              .filter((flag) => snapshot[flag.key] === true)
              .map((flag) => (
                <span className="tag" key={flag.label}>
                  {flag.label}
                </span>
              ))}
          </p>

          {isCompany ? null : <ContactLine full={snapshot.full} />}

          <p className="faint profile-head__asof">as of {snapshot.capturedAtDisplay}</p>

          <a
            className="table-action profile-head__link"
            href={snapshot.url}
            target="_blank"
            rel="noreferrer noopener"
          >
            <ExternalLinkIcon size={14} />
            <span className="table-action__label">Open on LinkedIn</span>
          </a>
        </div>
      </section>

      {snapshot.about !== null ? (
        <ProfileSection title="About">
          <p className="profile-text">{snapshot.about}</p>
        </ProfileSection>
      ) : null}

      {isCompany ? (
        <>
          <ProfileSection title="Company details">
            {companyDetails ?? <p className="muted">The scraper provided no company details.</p>}
            <TagGroup title="Specialties" values={specialities} />
          </ProfileSection>

          {locations.length > 0 ? (
            <ProfileSection title="Locations">
              <LocationList entries={locations} />
            </ProfileSection>
          ) : null}
        </>
      ) : null}

      {current.length > 0 ? (
        <ProfileSection title="Current positions">
          <PositionList entries={current} />
        </ProfileSection>
      ) : null}

      {experience.length > 0 ? (
        <ProfileSection title="Experience">
          <PositionList entries={experience} />
        </ProfileSection>
      ) : null}

      {education.length > 0 ? (
        <ProfileSection title="Education">
          <EducationList entries={education} />
        </ProfileSection>
      ) : null}

      {isCompany ? null : (
        <ProfileSection title="Skills & languages">
          <TagGroup title="Top skills" values={topSkills} />
          <TagGroup title="Skills" values={skills} />
          <TagGroup title="Languages" values={languages} />
          <TagGroup title="Certifications" values={certifications} />
          {topSkills.length + skills.length + languages.length + certifications.length === 0 ? (
            <p className="muted">The scraper provided no skills, languages or certifications.</p>
          ) : null}
        </ProfileSection>
      )}

      <section className="section">
        <div className="section-head">
          <h3 className="section-title">Change history</h3>
        </div>
        {changeLog.length === 0 ? (
          <div className="card">
            <div className="card__body">
              <p className="muted">
                No field changes detected yet — the log fills in as consecutive snapshots differ.
              </p>
            </div>
          </div>
        ) : (
          <div className="card card--table">
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Was</th>
                    <th>Became</th>
                    <th>Detected</th>
                  </tr>
                </thead>
                <tbody>
                  {changeLog.map((entry, index) => (
                    <tr key={`${entry.field}-${entry.detectedAt}-${index}`}>
                      <td>{entry.field}</td>
                      <td className="change-cell cell-muted">{entry.oldValue ?? '—'}</td>
                      <td className="change-cell">{entry.newValue ?? '—'}</td>
                      <td className="cell-muted">{entry.detectedAtDisplay}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

export default ProfileMode;
